import React, { forwardRef, ReactNode } from 'react';
import { animationTransition } from '@shared/utils/animationTransition';
import { Box, BoxProps, Flex, TextV2 } from '@withjoy/joykit';
import { Clock, Diamond } from '@withjoy/joykit/icons';
import { BOX_SHADOW_8_14_26 } from '@apps/card/constants/boxShadow';
import { Skeleton } from '@shared/components/Skeleton';
import { AspectRatioImage } from '../AspectRatioImage';
import { useIsMobileScreen } from '@shared/utils/media/useMediaScreens';
import useFavoriting from '../DesignsGallery/useFavoriting';
import { FavoriteButton } from '@apps/card/components/FavoriteButton/FavoriteButton';
import { FoilBadge } from '../FoilBadge';
import { StationeryTemplate } from '../DesignsGallery/DesignsGallery.types';
import { As } from '@shared/utils/forwardRef';

const AssetPreviewFrame = (props: { children?: React.ReactNode }) => {
  const isMobile = useIsMobileScreen();
  return (
    <Flex
      aspectRatio={isMobile ? '8 / 10' : '9 / 10'}
      overflow="hidden" // Needed for Safari
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      position="relative" // For badges
      paddingX={isMobile ? 4 : 8}
      paddingY={8}
      background="hsla(45, 12%, 93%, 1)"
      transition={animationTransition('background')}
      _groupHover={{
        background: 'hsla(45, 12%, 73%, 0.4)',
      }}
    >
      {props.children}
    </Flex>
  );
};

export const AssetPreview = (props: { assetUrl: string; children?: ReactNode }) => {
  const { assetUrl, children } = props;
  return (
    <AssetPreviewFrame>
      <AspectRatioImage
        src={assetUrl}
        borderRadius={1}
        boxShadow={BOX_SHADOW_8_14_26}
        transition={animationTransition('box-shadow')}
        _groupHover={{
          boxShadow: BOX_SHADOW_8_14_26,
        }}
      />
      {children}
    </AssetPreviewFrame>
  );
};

const AssetPreviewSkeleton = () => {
  // Matches AssetPreviewFrame dimensions.
  const isMobile = useIsMobileScreen();
  const aspectRatio = isMobile ? '8 / 10' : '9 / 10';
  return <Skeleton aspectRatio={aspectRatio} />;
};
AssetPreview.Skeleton = AssetPreviewSkeleton;

export type TagVariant = 'comingSoon' | 'printAvailable' | 'disabled' | 'printAndDigitalAvailable' | 'digitalOnly' | 'printAndPremiumDigitalAvailable';

interface TagProps {
  variant: TagVariant;
}

const TAG_VARIANT_MAP: Record<TagProps['variant'], { Icon: () => JSX.Element | null; label: string }> = {
  comingSoon: {
    Icon: () => (
      <Clock
        size="md"
        color="positive7"
      />
    ),
    label: 'Coming Soon',
  },
  printAvailable: {
    Icon: () => null,
    label: 'Paper Only',
  },
  printAndPremiumDigitalAvailable: {
    Icon: () => null,
    label: 'Paper & Digital',
  },
  disabled: {
    Icon: () => (
      <Clock
        size="md"
        color="positive7"
      />
    ),
    label: 'Disabled (Admin View Only)',
  },
  printAndDigitalAvailable: {
    Icon: () => null,
    label: 'Paper & Digital',
  },
  digitalOnly: {
    Icon: () => null,
    label: 'Digital Only',
  },
};

const Tag = (props: TagProps) => {
  const { Icon, label } = TAG_VARIANT_MAP[props.variant];
  return (
    <Flex
      alignItems="center"
      columnGap={1}
      paddingX={3}
      paddingY={1}
      borderRadius={40}
      backgroundColor="mono2"
    >
      <Icon />
      <TextV2
        as="span"
        typographyVariant="label2"
      >
        {label}
      </TextV2>
    </Flex>
  );
};

interface ContentProps {
  title: string;
  label: string | JSX.Element;
  children?: React.ReactNode;
  alignment?: 'left' | 'center';
}

const ContentContainer: React.FC<{ alignment: 'left' | 'center'; header: React.ReactNode; height?: BoxProps['height'] }> = ({ alignment, header, height, children }) => {
  return (
    <Flex
      flexDirection="column"
      rowGap={{ _: 1, md: 3 }}
      alignItems={alignment === 'left' ? 'flex-start' : 'center'}
      height={height}
    >
      <Flex
        flexDirection="column"
        rowGap={1}
        alignItems={alignment === 'left' ? 'flex-start' : 'center'}
        textAlign={alignment}
      >
        {header}
      </Flex>
      {children}
    </Flex>
  );
};
const Content = ({ children, label, title, alignment = 'center' }: ContentProps) => {
  return (
    <ContentContainer
      alignment={alignment}
      header={
        <>
          <TextV2 typographyVariant={{ _: 'hed1', md: 'hed2' }}>{title}</TextV2>
          {typeof label === 'string' ? <TextV2 typographyVariant="label2">{label}</TextV2> : <div>{label}</div>}
        </>
      }
    >
      {children}
    </ContentContainer>
  );
};

Content.Skeleton = ({ children, alignment = 'center' }: { children: React.ReactNode; alignment?: 'left' | 'center' }) => {
  return (
    <ContentContainer
      height={80}
      alignment={alignment}
      header={
        <>
          <Skeleton
            width={120}
            height={18}
          />
          <Skeleton
            width={50}
            height={16}
          />
        </>
      }
    >
      {children}
    </ContentContainer>
  );
};

export const GalleryTile = <T extends As = 'div'>(props: BoxProps<T>) => {
  return (
    <Box
      display="grid"
      alignItems="start"
      role="group"
      position="relative"
      cursor={props.onClick ? 'pointer' : undefined}
      {...props}
    />
  );
};

// Compound components + forwardRef + Typescript = a lot of extra typing, so working around it.
export const GalleryTileWithRef = forwardRef(<T extends As = 'div'>(props: BoxProps<T>, ref: React.Ref<HTMLDivElement>) => {
  return (
    <Box
      display="grid"
      alignItems="start"
      role="group"
      position="relative"
      cursor={props.onClick ? 'pointer' : undefined}
      {...props}
      ref={ref}
    />
  );
});

const PremiumBadge = () => {
  return (
    <Box
      typographyVariant="label1"
      fontWeight={600}
      position="absolute"
      display={'flex'}
      alignItems="center"
      right={4}
      bottom={4}
      color="white"
      userSelect="none"
      zIndex={1}
      title="Premium Digital Available"
    >
      <Diamond
        color="hsl(40, 60%, 60%)"
        size={20}
        transform="translateY(1px)"
      />
    </Box>
  );
};

interface FavoriteBadgeProps {
  eventId?: string;
  template: StationeryTemplate;
  onToggle?: (isFavorite: boolean) => void;
}

const FavoriteBadge = ({ eventId, onToggle, template }: FavoriteBadgeProps) => {
  const { isFavorite, toggleFavorite, isLoading } = useFavoriting({
    isFavorite: template.isFavorite,
    templateId: template.id,
    eventId: eventId || '',
  });

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    onToggle?.(!isFavorite);

    toggleFavorite();
  };

  if (!eventId) {
    return null;
  }

  return (
    <FavoriteButton
      position="absolute"
      top={3}
      right={3}
      isFavorite={isFavorite}
      isLoading={isLoading}
      onClick={handleClick}
    />
  );
};

const TileFoilBadge = () => {
  return (
    <Box
      position="absolute"
      top={4}
      left={3}
    >
      <FoilBadge />
    </Box>
  );
};

GalleryTile.Skeleton = () => {
  return (
    <GalleryTile rowGap={6}>
      <AssetPreview.Skeleton />
      <Content.Skeleton>
        <Skeleton
          width={70}
          height={18}
        />
      </Content.Skeleton>
    </GalleryTile>
  );
};

GalleryTile.AssetPreview = AssetPreview;
GalleryTile.Content = Content;
GalleryTile.Tag = Tag;
GalleryTile.PremiumBadge = PremiumBadge;
GalleryTile.FavoriteBadge = FavoriteBadge;
GalleryTile.FoilBadge = TileFoilBadge;
